import { computed, action, observable, runInAction } from 'mobx';
import ErrorUtil from '../error/ErrorUtil';
import { MOBILE_RECOMMENDATIONS_SEARCH_GA, VOLTE_RECOMMENDATIONS_SEARCH_GA } from '../offer/proposals/ProposalsGaEvents';
import API from '../util/ApiUtil';
import RootStore from '../stores/RootStore';
import GaDataStore from './GaDataStore';
import LanguageStore from './LanguageStore';
import RouteStore from './RouteStore';

export interface MobileRecommendation {
  mobileUser: string;
  mobileNumber: number;
  currentDeviceName: string;
  deviceAge: Seniority;
  totalDeviceAgeInDays: number;
  recommendedDeviceName: string;
  price: string;
  redirectUrl: string;
}

export interface VolteRecommendation {
  mobileNumber: number;
  deviceName: string;
  redirectUrl: string;
}

export interface Seniority {
  years: number;
  months: number;
  days: number;
}

export type ProposalType = 'MOB' | 'VOLTE';

interface Proposals {
  mobRecommendations?: MobileRecommendation[];
  volteRecommendations?: VolteRecommendation[];
}

class ProposalStore {
  @observable accessor isProposalModalOpen = false;
  @observable accessor recommendations: Proposals;
  @observable accessor filteredRecommendations: Proposals;
  @observable accessor searchKeys: { mobKey?: string; volteKey?: string };
  @observable accessor excelType: string = '';
  @observable accessor proposalsStatus: string = 'UNINITIALIZED';
  @observable accessor excelStatus: string = 'UNINITIALIZED';

  private routeStore: RouteStore;
  private languageStore: LanguageStore;

  constructor(rootStore: RootStore) {
    this.routeStore = rootStore.routeStore;
    this.languageStore = rootStore.languageStore;
  }

  public toggleProposalModal = (open: boolean) => {
    runInAction(() => {
      this.routeStore.push(this.routeStore.pathname, open ? { showMobileRecommendations: 'true' } : null);
      this.isProposalModalOpen = open;
    });
  };

  @action
  public reset() {
    this.recommendations = undefined;
    this.filteredRecommendations = undefined;
    this.searchKeys = undefined;
  }

  public fetchProposals = async () => {
    runInAction(() => {
      this.proposalsStatus = 'FETCHING';
    });
    try {
      const response = await API.get('/proposal');
      runInAction(() => {
        this.recommendations = response.data;
        this.filteredRecommendations = response.data;
        this.proposalsStatus = 'FETCHED';
      });
    } catch (e) {
      runInAction(() => {
        ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
        this.recommendations = undefined;
        this.filteredRecommendations = undefined;
        this.proposalsStatus = 'FAILED';
      });
    }
  };

  public fetchMobileRecommendationsExcel = async (type: ProposalType) => {
    runInAction(() => {
      this.excelStatus = 'FETCHING';
      this.excelType = type;
    });
    const lang = this.languageStore.currentLanguage;
    try {
      const { data } = await API.post(
        `/proposal/${lang}/excel/${type.toLowerCase()}`,
        type === 'MOB' ? this.filteredRecommendations.mobRecommendations : this.filteredRecommendations.volteRecommendations,
        {
          responseType: 'blob',
          timeout: 30000
        }
      );
      runInAction(() => {
        this.excelStatus = 'FETCHED';
      });
      return data;
    } catch (e) {
      runInAction(() => {
        this.excelStatus = 'FAILED';
      });
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
    }
  };

  public searchMobRecs = () => {
    if (!this.searchKeys?.mobKey) {
      runInAction(() => {
        this.filteredRecommendations.mobRecommendations = this.recommendations.mobRecommendations;
      });
      return;
    }

    const searchKey = this.searchKeys.mobKey.toLowerCase();
    runInAction(() => {
      this.filteredRecommendations.mobRecommendations = this.recommendations.mobRecommendations.filter((r) => {
        return (
          r.recommendedDeviceName.toLowerCase().includes(searchKey) ||
          r.mobileUser.toLowerCase().includes(searchKey) ||
          r.mobileNumber.toString().includes(searchKey) ||
          r.currentDeviceName.toLowerCase().includes(searchKey)
        );
      });
    });
    GaDataStore.pushWithoutCmsData(MOBILE_RECOMMENDATIONS_SEARCH_GA, {
      searchKey
    });
  };

  public searchVolteRecs = () => {
    if (!this.searchKeys?.volteKey) {
      runInAction(() => {
        this.filteredRecommendations.volteRecommendations = this.recommendations.volteRecommendations;
      });
      return;
    }

    const searchKey = this.searchKeys.volteKey.toLowerCase();
    runInAction(() => {
      this.filteredRecommendations.volteRecommendations = this.recommendations.volteRecommendations.filter((r) => {
        return r.mobileNumber.toString().includes(searchKey) || r.deviceName.toLowerCase().includes(searchKey);
      });
    });
    GaDataStore.pushWithoutCmsData(VOLTE_RECOMMENDATIONS_SEARCH_GA, {
      searchKey
    });
  };

  public updateSearchValue = (key: string, type: ProposalType) => {
    runInAction(() => {
      this.searchKeys = { ...(this.searchKeys ?? {}), [type === 'MOB' ? 'mobKey' : 'volteKey']: key };
    });
  };

  @computed
  get proposalsFetching(): boolean {
    return ['UNINITIALIZED', 'FETCHING'].includes(this.proposalsStatus);
  }

  @computed
  get hasProposals(): boolean {
    return this.hasVolteProposals || this.hasMobProposals;
  }

  @computed
  get hasVolteProposals(): boolean {
    return !!this.recommendations?.volteRecommendations?.length;
  }

  @computed
  get hasMobProposals(): boolean {
    return !!this.recommendations?.mobRecommendations?.length;
  }

  @computed
  get recommendationsFetchFailed(): boolean {
    return this.proposalsStatus === 'FAILED';
  }

  @computed
  get isGeneratingExcel(): boolean {
    return this.excelStatus === 'FETCHING';
  }
}

export default ProposalStore;
